// GA4 Analytics Module — GDPR-compliant, SPA-aware
const GA_MEASUREMENT_ID = 'G-E6PPPD1MEG';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/** Check if user has given full cookie consent */
export const hasAnalyticsConsent = (): boolean => {
  return localStorage.getItem('cookie-consent') === 'all';
};

/** Check if gtag script is already loaded */
const isGtagLoaded = (): boolean => {
  return !!document.querySelector(`script[src*="googletagmanager.com/gtag"]`);
};

/** Load and initialize GA4 script */
export const initializeGA = (): void => {
  if (!hasAnalyticsConsent() || isGtagLoaded()) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false, // We send page_view manually for SPA
  });
};

/** Send a page_view event (SPA navigation) */
export const trackPageView = (path: string, title?: string): void => {
  if (!hasAnalyticsConsent() || typeof window.gtag !== 'function') return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title || document.title,
    page_location: window.location.origin + path,
  });
};

/** Send a custom event */
export const trackEvent = (
  eventName: string,
  params?: Record<string, string | number | boolean>
): void => {
  if (!hasAnalyticsConsent() || typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, params);
};

// Pre-built event helpers
export const trackCTAClick = (ctaName: string, location: string) =>
  trackEvent('cta_click', { cta_name: ctaName, cta_location: location });

export const trackFormSubmit = (formName: string) =>
  trackEvent('form_submit', { form_name: formName });

export const trackContactClick = (method: string) =>
  trackEvent('contact_click', { contact_method: method });
